import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, X } from "lucide-react";
import { DEVELOPMENTS, devSpecs } from "../lib/developments";

function Lightbox({ photos, index, setIndex }) {
  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIndex(null);
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, photos.length, setIndex]);
  if (index === null) return null;
  const nav = (step) => (e) => { e.stopPropagation(); setIndex((i) => (i + step + photos.length) % photos.length); };
  return (
    <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10" onClick={() => setIndex(null)} data-testid="project-lightbox">
      <img src={photos[index]} alt="" style={{ width: "min(85vw, calc((100vh - 6rem) * 1.5))" }} className="aspect-[3/2] rounded-lg object-cover" onClick={(e) => e.stopPropagation()} data-testid="project-lightbox-image" />
      <button onClick={() => setIndex(null)} className="absolute top-5 right-5 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="Close" data-testid="project-lightbox-close"><X className="h-5 w-5" /></button>
      <button onClick={nav(-1)} className="absolute left-3 md:left-6 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="Previous" data-testid="project-lightbox-prev"><ChevronLeft className="h-5 w-5" /></button>
      <button onClick={nav(1)} className="absolute right-3 md:right-6 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="Next" data-testid="project-lightbox-next"><ChevronRight className="h-5 w-5" /></button>
      <div className="absolute bottom-5 font-mono2 text-xs text-white/60">{index + 1} / {photos.length}</div>
    </div>
  );
}

function Specs({ d }) {
  return (
    <dl className="divide-y divide-white/[0.06] border-y border-white/[0.06]" data-testid="project-specs">
      {devSpecs(d).map(([k, v]) => (
        <div key={k} className="flex items-baseline justify-between gap-6 py-3.5">
          <dt className="font-mono2 text-[11px] uppercase tracking-widest text-white">{k}</dt>
          <dd className="text-base text-white text-right">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const [index, setIndex] = useState(null);
  const [active, setActive] = useState(0);
  const i = DEVELOPMENTS.findIndex((x) => x.slug === slug);
  useEffect(() => { window.scrollTo(0, 0); setActive(0); }, [slug]);
  if (i < 0) return <Navigate to="/" replace />;
  const d = DEVELOPMENTS[i];
  const photos = [d.img, ...d.gallery];
  const next = DEVELOPMENTS[(i + 1) % DEVELOPMENTS.length];
  return (
    <div className="min-h-screen bg-[#08100E] text-white" data-testid="project-detail-page">
      <header className="fixed top-0 inset-x-0 z-50 bg-[#08100E]/85 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="project-logo"><img src="/logo.png" alt="ARQITEK" className="h-6 md:h-7 w-auto" /></Link>
          <Link to="/developments" className="flex items-center gap-2 text-sm text-[#A5B2AE] hover:text-white transition-colors" data-testid="project-back-link"><ArrowLeft className="h-4 w-4" /> All developments</Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 pt-28 md:pt-32 pb-10 md:pb-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono2 text-[11px] uppercase tracking-widest text-[#27BEAA]">{d.city} · {d.type}</div>
            <h1 className="font-clash text-4xl sm:text-5xl font-semibold tracking-tight mt-3" data-testid="project-name">{d.name}</h1>
          </div>
          <span className="rounded-full border border-[#27BEAA]/40 bg-[#27BEAA]/10 px-4 py-1.5 font-mono2 text-[11px] uppercase tracking-widest text-[#27BEAA]" data-testid="project-status">{d.status}</span>
        </div>

        <div className="mt-10 grid lg:grid-cols-[1.6fr_1fr] gap-10 lg:gap-14 items-start">
          <div>
            <button onClick={() => setIndex(active)} className="group block w-full overflow-hidden rounded-xl border border-white/[0.06]" data-testid="project-main-image">
              <img src={photos[active]} alt={d.name} className="w-full aspect-[16/10] object-cover transition-transform duration-700 group-hover:scale-[1.02]" />
            </button>
            <div className="mt-3 grid grid-cols-4 gap-3" data-testid="project-gallery">
              {photos.map((p, k) => (
                <button key={p} onClick={() => setActive(k)} aria-pressed={k === active} data-testid={`project-gallery-item-${k}`}
                  className={`overflow-hidden rounded-lg border transition-[border-color,opacity] ${k === active ? "border-[#27BEAA] opacity-100" : "border-white/[0.06] opacity-60 hover:opacity-100"}`}>
                  <img src={p} alt={`${d.name} view ${k + 1}`} loading="lazy" className="w-full aspect-[16/10] object-cover" />
                </button>
              ))}
            </div>
          </div>

          <aside className="lg:sticky lg:top-24">
            <div className="font-mono2 text-[11px] uppercase tracking-widest text-[#8AA09A]">Overview</div>
            <p className="mt-4 text-base leading-relaxed text-[#C9D3D0]" data-testid="project-overview">{d.overview}</p>
            {d.features && <p className="mt-4 text-sm text-[#8AA09A]" data-testid="project-features">{d.features}</p>}
            <div className="mt-8 font-mono2 text-[11px] uppercase tracking-widest text-white mb-2">Key Specs</div>
            <Specs d={d} />
          </aside>
        </div>
      </main>

      <section className="border-t border-white/[0.06]">
        <Link to={`/developments/${next.slug}`} className="group max-w-7xl mx-auto px-6 md:px-12 py-10 flex items-center justify-between" data-testid="project-next-link">
          <div>
            <div className="font-mono2 text-[11px] uppercase tracking-widest text-[#66736F]">Next project</div>
            <div className="font-clash text-2xl md:text-3xl mt-2 group-hover:text-[#27BEAA] transition-colors">{next.name}</div>
          </div>
          <ChevronRight className="h-6 w-6 text-[#27BEAA] transition-transform group-hover:translate-x-1" />
        </Link>
      </section>

      <Lightbox photos={photos} index={index} setIndex={setIndex} />
    </div>
  );
}
